import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { MagnifyingGlass, Car } from "@phosphor-icons/react";

const money = (v) => (v == null || v === "" ? "—" : `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
};

const statusStyle = (s) => {
  const key = (s || "").toLowerCase();
  if (key === "completed") return "text-emerald-400";
  if (key === "cancelled" || key === "rejected") return "text-destructive";
  if (key === "confirmed" || key === "current") return "text-primary";
  if (key === "submitted" || key === "pre-booking" || key === "pre_booking") return "text-amber-400";
  return "text-muted-foreground";
};

const bucketOf = (b) => {
  const s = (b.status || "").toLowerCase();
  if (s === "completed") return "completed";
  if (s === "cancelled" || s === "rejected") return "past";
  const now = Date.now();
  const start = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
  const end = b.end_datetime ? new Date(b.end_datetime).getTime() : 0;
  if (end && end < now) return "past";
  if (start && start > now) return "upcoming";
  return "current";
};

const carLabel = (v) => [v.year, v.make, v.model].filter(Boolean).join(" ") + (v.license_plate ? ` · ${v.license_plate}` : "");

export default function InvestorBookings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [carId, setCarId] = useState("all");

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["investor-bookings"],
    queryFn: async () => (await api.get("/bookings")).data,
    refetchOnWindowFocus: true,
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["investor-vehicles"],
    queryFn: async () => (await api.get("/vehicles")).data,
  });

  // Auto-select the first (and only) car when there's just one
  useEffect(() => {
    if (vehicles.length === 1) setCarId(vehicles[0].id);
  }, [vehicles]);

  const carBookings = useMemo(() => {
    if (carId === "all") return bookings;
    return bookings.filter((b) => b.vehicle_id === carId);
  }, [bookings, carId]);

  const buckets = useMemo(() => {
    const out = { upcoming: [], current: [], completed: [], past: [] };
    for (const b of carBookings) out[bucketOf(b)].push(b);
    for (const k of Object.keys(out)) out[k].sort((a, b) => (a.start_datetime || "").localeCompare(b.start_datetime || ""));
    return out;
  }, [carBookings]);

  const list = (buckets[tab] || []).filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [b.customer_name, b.vehicle_label, b.status, b.booking_type].filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
  });

  const countFor = (id) => (id === "all" ? bookings.length : bookings.filter((b) => b.vehicle_id === id).length);

  return (
    <div className="space-y-6" data-testid="investor-bookings-page">
      <div>
        <div className="label-mono">Investor Portal</div>
        <h1 className="font-heading text-3xl mt-1">Bookings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {vehicles.length === 0
            ? "No cars linked to your account yet — ask the admin to link one."
            : vehicles.length === 1
              ? `All bookings for your car (${carLabel(vehicles[0])}).`
              : `Switch between your ${vehicles.length} cars using the tabs below.`}
        </p>
      </div>

      {vehicles.length > 1 && (
        <div className="border border-border bg-card rounded-md overflow-x-auto" data-testid="investor-car-switcher">
          <div className="flex items-center gap-1 p-2">
            <button
              onClick={() => setCarId("all")}
              className={`px-4 py-2 rounded-md text-sm font-heading whitespace-nowrap transition-colors ${carId === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
              data-testid="car-tab-all"
            >
              All cars <span className="ml-2 text-xs opacity-70">{countFor("all")}</span>
            </button>
            {vehicles.map((v) => (
              <button
                key={v.id}
                onClick={() => setCarId(v.id)}
                className={`px-4 py-2 rounded-md text-sm font-heading whitespace-nowrap transition-colors ${carId === v.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
                data-testid={`car-tab-${v.id}`}
              >
                {carLabel(v)} <span className="ml-2 text-xs opacity-70">{countFor(v.id)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search customer, status…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-input border-border" data-testid="investor-bookings-search" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming <span className="ml-2 text-xs text-muted-foreground">{buckets.upcoming.length}</span></TabsTrigger>
          <TabsTrigger value="current" data-testid="tab-current">Current <span className="ml-2 text-xs text-muted-foreground">{buckets.current.length}</span></TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed <span className="ml-2 text-xs text-muted-foreground">{buckets.completed.length}</span></TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">Past / Cancelled <span className="ml-2 text-xs text-muted-foreground">{buckets.past.length}</span></TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-6 py-3 label-mono font-normal">Start</th>
              <th className="text-left px-6 py-3 label-mono font-normal">End</th>
              <th className="text-left px-6 py-3 label-mono font-normal">Customer</th>
              <th className="text-left px-6 py-3 label-mono font-normal">Vehicle</th>
              <th className="text-left px-6 py-3 label-mono font-normal">Type</th>
              <th className="text-left px-6 py-3 label-mono font-normal">Status</th>
              <th className="text-right px-6 py-3 label-mono font-normal">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">
                <Car size={28} className="mx-auto mb-2 opacity-40" />
                No bookings in this bucket.
              </td></tr>
            ) : list.map((b) => (
              <tr key={b.id} className="border-b border-border/40 hover:bg-secondary/40 cursor-pointer" onClick={() => navigate(`/bookings/view/${b.id}`)} data-testid={`investor-booking-row-${b.id}`}>
                <td className="px-6 py-3 font-mono-data text-xs">{fmtDate(b.start_datetime)}</td>
                <td className="px-6 py-3 font-mono-data text-xs">{fmtDate(b.end_datetime)}</td>
                <td className="px-6 py-3 font-heading">{b.customer_name || "—"}</td>
                <td className="px-6 py-3">{b.vehicle_label || "—"}</td>
                <td className="px-6 py-3 capitalize text-muted-foreground">{b.booking_type || "—"}</td>
                <td className={`px-6 py-3 label-mono ${statusStyle(b.status)}`}>{b.status || "—"}</td>
                <td className="px-6 py-3 text-right font-mono-data">{money(b.total || b.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
